import React, { useState } from 'react';

import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import ApiClient from '@lib/apiClient';

import {
	changeDateFormat,
	getFileType,
	getIconByFileType,
	getShortName,
	onImageLoadError,
	openNewWindow
} from '@utils/Util';

const EtdApprovalInfo = ({ etdApprovalInfo, getEtdApprovalInfo, onActionDone }) => {
	const [isLoading, setIsLoading] = useState(false);

	const proofDocument = etdApprovalInfo?.proofDocument;
	const proofFileType = getFileType(proofDocument?.docUrl);
	const proofThumbnail =
		proofFileType === 'IMAGE' ? proofDocument?.docUrl : getIconByFileType(proofFileType);
	const pendingApprovers = etdApprovalInfo?.pendingApprovers || [];
	const showAwaitingApprover =
		etdApprovalInfo?.status === 'PENDING' && !etdApprovalInfo?.canApprove && pendingApprovers.length > 0;

	const handleApproveOrReject = (newStatus) => {
		const requestId = etdApprovalInfo?.id;
		if (!requestId) return;
		setIsLoading(true);
		ApiClient.PUT('approveRfqUpdateRequest', Number(requestId), `?newStatus=${newStatus}`)
			.then(() => {
				toastSuccess(
					newStatus === 'APPROVED' ? 'ETD change request approved' : 'ETD change request rejected'
				);
				getEtdApprovalInfo(requestId);
				if (onActionDone) {
					onActionDone();
				}
				setIsLoading(false);
			})
			.catch((error) => {
				setIsLoading(false);
				if (error?.response?.data?.message) {
					toastError(error.response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	return (
		<LoaderComponent loading={isLoading}>
			<div className="order-cancel-info d-flex align-items-center">
				<div className="from-info pr-2">
					<p className="regular-14 gray_dark_02 mb-1">From </p>
					<p className="regular-14 gray_dark_01">
						{etdApprovalInfo?.requestedBy}{' '}
						<span className="gray_dark_02">
							{changeDateFormat(etdApprovalInfo?.requestedAt, 'YYYY-MM-DD', 'DD MMM, YY')}
						</span>
					</p>
				</div>
				<div className="reason pl-3 border-left border-right">
					<p className="regular-14 gray_dark_01 mb-1">ETD change request</p>
					<p className="regular-14 gray_dark_02">
						{etdApprovalInfo?.oldEtd
							? changeDateFormat(etdApprovalInfo?.oldEtd, 'YYYY-MM-DD', 'DD MMM, YY')
							: '-'}{' '}
						→{' '}
						{etdApprovalInfo?.newEtd
							? changeDateFormat(etdApprovalInfo?.newEtd, 'YYYY-MM-DD', 'DD MMM, YY')
							: '-'}
						{etdApprovalInfo?.etdChangeReason ? ` · ${etdApprovalInfo.etdChangeReason}` : ''}
					</p>
				</div>
				{proofDocument?.docUrl && (
					<div className="proof-document pl-3 pr-3 border-right">
						<p className="regular-14 gray_dark_02 mb-1">Proof</p>
						<div
							className="proof-document-file d-flex align-items-center cursor-pointer"
							onClick={() => openNewWindow(proofDocument.docUrl)}
							title={proofDocument?.originalName || proofDocument?.name}
						>
							<img src={proofThumbnail} alt="Proof" onError={onImageLoadError} />
							<span className="regular-14 gray_dark_01 ml-2">
								{getShortName(
									proofDocument?.originalName || proofDocument?.name || 'View',
									14
								)}
							</span>
						</div>
					</div>
				)}
				<div className="approve-reject-action d-flex justify-content-end pl-4">
					{etdApprovalInfo?.status === 'PENDING' && etdApprovalInfo?.canApprove && (
						<button
							className="button revise size32 mr-2"
							onClick={() => handleApproveOrReject('REJECTED')}
							disabled={isLoading}
						>
							Reject
						</button>
					)}
					{etdApprovalInfo?.status === 'PENDING' && etdApprovalInfo?.canApprove && (
						<button
							onClick={() => handleApproveOrReject('APPROVED')}
							className="button approve size32 ml-1"
							disabled={isLoading}
						>
							Approve
						</button>
					)}
					{etdApprovalInfo?.status === 'APPROVED' && (
						<button className="button approved size32">
							<span className="approved-text">Approved</span>
						</button>
					)}
					{etdApprovalInfo?.status === 'REJECTED' && (
						<button className="button approved rejected size32">
							<span className="approved-text">Rejected</span>
						</button>
					)}
					{showAwaitingApprover && (
						<p className="awaiting-approval regular-14 gray_dark_02">
							Awaiting approval from{' '}
							<span className="gray_dark_01">{pendingApprovers.join(', ')}</span>
						</p>
					)}
				</div>
			</div>
		</LoaderComponent>
	);
};

export default EtdApprovalInfo;
